import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Send, Search } from "lucide-react";

function EmailPreview({ businessName, logoDataUrl, greeting, buttonText }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        {logoDataUrl ? <img src={logoDataUrl} alt="logo" className="h-10 mb-3 object-contain" /> : <div className="h-10 w-28 bg-slate-100 rounded mb-3" />}
        <div className="font-display font-bold text-lg text-slate-900 mb-2">How did we do, [First name]?</div>
        <p className="text-sm text-slate-600 mb-2">{greeting || "Thanks so much for choosing us — it was a pleasure working with you."}</p>
        <p className="text-sm text-slate-600 mb-4">Would you take 30 seconds to share your experience on Google? It genuinely helps <strong>{businessName || "your business"}</strong>.</p>
        <div className="my-4"><span className="inline-block bg-primary text-white rounded-full px-5 py-2.5 text-sm font-semibold">{buttonText || "Leave us a review"}</span></div>
      </div>
    </div>
  );
}

export default function SendRequest() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [reminder, setReminder] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [tpl, setTpl] = useState({ greeting: "", button_text: "" });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/email-template").then((r) => setTpl(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  const send = async () => {
    if (selected.size === 0) { toast.error("Pick at least one customer"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/requests/send", {
        customer_ids: Array.from(selected),
        reminder_enabled: reminder,
      });
      const msg = data.message || `${data.queued} queued`;
      const suppressed = data.suppressed ? ` · ${data.suppressed} skipped (unsubscribed)` : "";
      toast.success(msg + suppressed);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail?.banner || e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="send-page">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Send review requests</h1>
        <p className="text-slate-500 mt-1">Pick your customers, preview the email, send.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-12" data-testid="send-search" />
            </div>
            <label className="text-sm text-slate-600 inline-flex items-center gap-2 whitespace-nowrap">
              <Checkbox
                checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                onCheckedChange={(v) => {
                  const s = new Set(selected);
                  filtered.forEach((c) => v ? s.add(c.id) : s.delete(c.id));
                  setSelected(s);
                }}
                data-testid="send-select-all"
              />
              Select all
            </label>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-500" data-testid="send-empty">
              No customers yet. Add one on the Customers page.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 border rounded-xl overflow-hidden">
              {filtered.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => toggle(c.id)} data-testid={`send-row-${c.id}`}>
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-sm text-slate-500 truncate">{c.email}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between pt-2">
            <label className="inline-flex items-center gap-3 text-sm">
              <Switch checked={reminder} onCheckedChange={setReminder} data-testid="send-reminder-toggle" />
              <span>Send a polite reminder after 3 days if they don't click</span>
            </label>
            <Button onClick={send} disabled={busy || selected.size === 0} className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90" data-testid="send-submit">
              <Send className="h-4 w-4 mr-2" /> {busy ? "Sending…" : `Send ${selected.size ? `(${selected.size})` : ""}`}
            </Button>
          </div>
        </Card>

        <Card className="rounded-2xl p-6">
          <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Preview</div>
          <EmailPreview
            businessName={user?.business_name}
            logoDataUrl={user?.logo_data_url}
            greeting={tpl.greeting}
            buttonText={tpl.button_text}
          />
        </Card>
      </div>
    </div>
  );
}
